/**
 * Workstream 0 — ad click-ID + UTM capture for the recruiting funnel.
 *
 * Without this, a contracted agent cannot be attributed to the campaign that
 * produced them: the hub turns a completed contract into a Google/Meta
 * conversion using the `gclid` carried in the PandaDoc document's metadata, so
 * the click ID has to survive from the landing page all the way to the create
 * call. Nothing else in the chain can recover it afterwards.
 *
 * Ported from medicare-advocates-web's `src/lib/utm.ts`, which the handoff says
 * to mirror — but NOT verbatim. That implementation has a confirmed bug and
 * three known weaknesses, all fixed here (see FIXES below). Their session
 * diagnosed it while reviewing this port; the fixes are theirs, credited.
 *
 * FIXES vs the consumer-site original
 * -----------------------------------
 * 1. LAST-TOUCH NO LONGER WIPES CLICK IDS. The original refreshed last-touch
 *    whenever the URL had any param KEY, and REPLACED the stored record. So:
 *      organic visit -> paid click ?gclid=ABC -> any url with any param (even
 *      an empty `?q=`) -> gclid gone.
 *    It only survived when the first touch happened to carry the same id. Here
 *    the refresh is gated on non-empty VALUES, and merges so a param-bearing
 *    internal navigation can never blank an existing click ID. A genuinely new
 *    paid click still overwrites with its own.
 * 2. SEPARATE TTLs. One 30-day TTL for both records meant "first touch" really
 *    meant "first touch in the last 30 days" — wrong for a recruiting cycle
 *    measured in months. Last-touch keeps 30 days; first-touch gets 180.
 * 3. COOKIE MIRROR. localStorage alone loses attribution silently when storage
 *    is blocked (private browsing, strict settings) and the lead posts with
 *    nothing. The click IDs are mirrored to a cookie as a fallback.
 * 4. BOUNDED PARAM COUNT. Every captured param rides downstream, so the count
 *    is capped as well as key/value length.
 *
 * Capture everything, expose selectively: msclkid/ttclid are stored even though
 * AgentBoost runs no Microsoft/TikTok ads today (confirmed with the owner
 * 2026-10-09) and the GTM container has no variables for them. Storage costs
 * nothing and a dataLayer cannot be backfilled, so if those channels ever
 * switch on, the history is already there — only the exposure list changes.
 */

const FIRST_KEY = "abm_attr_first";
const LAST_KEY = "abm_attr_last";
const COOKIE_KEY = "abm_attr";
/** Stable first-party browser id (see getVisitorId). */
const VISITOR_KEY = "abm_vid";

const LAST_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — the converting click
const FIRST_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days — original source
const FIRST_TTL_SECONDS = FIRST_TTL_MS / 1000;

const MAX_KEY_LEN = 128;
const MAX_VALUE_LEN = 512;
const MAX_PARAMS = 40;

/**
 * Params that are ours, not a marketing touch. A URL carrying only these must
 * never refresh last-touch, or an internal navigation would overwrite the
 * converting click's context. Keep this list current as internal links grow.
 */
const INTERNAL_PARAMS = new Set(["content", "api", "q", "page", "ref"]);

/**
 * Ad click identifiers. These are the values the hub joins on, so they get the
 * cookie mirror and are never dropped by a later touch that lacks them.
 */
export const CLICK_ID_PARAMS = [
    "gclid",
    "wbraid",
    "gbraid",
    "fbclid",
    "msclkid",
    "ttclid",
] as const;

/**
 * Exposed as persistent GTM Data Layer Variables. Deliberately NOT the same as
 * CLICK_ID_PARAMS: only these have variables defined in GTM-WS5BKHBX, and a
 * push with no variable behind it goes nowhere. `rid` is the recruiter code —
 * the durable join that resolves a completed contract to a recruiter.
 */
export const DATALAYER_VARS = [
    "gclid",
    "wbraid",
    "gbraid",
    "fbclid",
    "rid",
    "experiment",
    "arm",
] as const;

export type Attribution = Record<string, string | undefined>;

type Stored = { data: Attribution; expires_at: number };

function readStored(key: string): Attribution | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const stored: Stored = JSON.parse(raw);
        if (Date.now() > stored.expires_at) {
            localStorage.removeItem(key);
            return null;
        }
        return stored.data;
    } catch {
        return null;
    }
}

function writeStored(key: string, data: Attribution, ttl: number): void {
    try {
        localStorage.setItem(
            key,
            JSON.stringify({ data, expires_at: Date.now() + ttl }),
        );
    } catch {
        // Storage blocked — the cookie mirror below is the fallback.
    }
}

/** Mirror just the join keys to a cookie, so blocked storage doesn't lose them. */
function writeCookie(data: Attribution): void {
    try {
        const keep: Attribution = {};
        for (const k of [...CLICK_ID_PARAMS, "rid", "utm_campaign"]) {
            if (data[k]) keep[k] = data[k];
        }
        if (Object.keys(keep).length === 0) return;
        const value = encodeURIComponent(JSON.stringify(keep));
        const maxAge = Math.floor(LAST_TTL_MS / 1000);
        document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } catch {
        // ignore
    }
}

/** One raw cookie value by name, decoded. "" when absent. */
function readRawCookie(name: string): string {
    try {
        const m = document.cookie.match(
            new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}=([^;]*)`),
        );
        return m ? decodeURIComponent(m[1]) : "";
    } catch {
        return "";
    }
}

function readCookie(): Attribution {
    try {
        const match = document.cookie.match(
            new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]*)`),
        );
        if (!match) return {};
        return JSON.parse(decodeURIComponent(match[1])) as Attribution;
    } catch {
        return {};
    }
}

/**
 * Capture the current URL's params. Call on every navigation — a visitor can
 * land on an ad URL anywhere in the site, not just the home page.
 */
export function captureParams(search: string, href: string): void {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(search);
    const captured: Attribution = {};

    let count = 0;
    for (const [key, val] of params.entries()) {
        if (!val || key.length > MAX_KEY_LEN) continue;
        if (count >= MAX_PARAMS) break;
        captured[key] = val.slice(0, MAX_VALUE_LEN);
        count++;
    }

    captured.landing_url = href.slice(0, MAX_VALUE_LEN);
    captured.landing_path = window.location.pathname;
    if (document.referrer) captured.referrer = document.referrer.slice(0, MAX_VALUE_LEN);
    captured.captured_at = new Date().toISOString();

    // FIRST touch: written once, then never overwritten.
    if (!readStored(FIRST_KEY)) writeStored(FIRST_KEY, captured, FIRST_TTL_MS);

    // LAST touch: refresh only on a real external marketing touch. Gated on
    // non-empty VALUES (an empty `?q=` is not a touch) and on params that are
    // not ours. This is fix #1 — the original read bare keys and wiped ids.
    const isMarketingTouch = [...params.entries()].some(
        ([k, v]) => v && !INTERNAL_PARAMS.has(k),
    );
    if (!isMarketingTouch) return;

    // MERGE, never replace: a new touch overwrites with its own click IDs, but
    // one that carries none must not blank the ids already stored.
    const previous = readStored(LAST_KEY) ?? {};
    const merged: Attribution = { ...previous, ...captured };
    const incomingHasClickId = CLICK_ID_PARAMS.some((k) => captured[k]);
    if (!incomingHasClickId) {
        for (const k of CLICK_ID_PARAMS) {
            if (previous[k]) merged[k] = previous[k];
        }
    }

    writeStored(LAST_KEY, merged, LAST_TTL_MS);
    writeCookie(merged);
}

/**
 * The attribution to send with a conversion. Last-touch wins (it is the
 * converting click), first-touch fills gaps, the cookie backstops both.
 */
export function getAttribution(): Attribution {
    if (typeof window === "undefined") return {};
    const first = readStored(FIRST_KEY) ?? {};
    const last = readStored(LAST_KEY) ?? {};
    const cookie = readCookie();

    const merged: Attribution = { ...cookie, ...first, ...last };

    // The cookie is the fallback when localStorage was unavailable at capture:
    // only let it supply a click ID that storage does not already have.
    for (const k of CLICK_ID_PARAMS) {
        if (!merged[k] && cookie[k]) merged[k] = cookie[k];
    }

    if (first.utm_source) merged.first_utm_source = first.utm_source;
    if (first.utm_campaign) merged.first_utm_campaign = first.utm_campaign;
    if (first.landing_url) merged.first_landing_url = first.landing_url;
    if (first.captured_at) merged.first_captured_at = first.captured_at;

    return merged;
}

/** Just the click IDs + rid, for the PandaDoc document metadata. */
export function getJoinKeys(): Record<string, string> {
    const attr = getAttribution();
    const out: Record<string, string> = {};
    for (const k of [...CLICK_ID_PARAMS, "rid", "utm_campaign"]) {
        if (attr[k]) out[k] = attr[k] as string;
    }
    return out;
}

/**
 * A stable first-party id for this browser, created on first visit.
 *
 * WHY: click IDs only exist for PAID traffic. An agent who arrives organically,
 * direct, or from a referral has no join key at all beyond email/NPN — and
 * `rid` only when a recruiter link was used. `visitor_id` gives every contract
 * a key back to its own sessions regardless of source, and it is not subject to
 * Google's 90-day click window, so a contract signed months after first touch
 * can still be joined in the warehouse.
 *
 * Mirrored to a cookie for the same reason the join keys are: localStorage can
 * be blocked, and losing the id silently is worse than not having one.
 */
export function getVisitorId(): string {
    try {
        const existing = localStorage.getItem(VISITOR_KEY);
        if (existing) return existing;
        const fromCookie = readRawCookie(VISITOR_KEY);
        if (fromCookie) {
            localStorage.setItem(VISITOR_KEY, fromCookie);
            return fromCookie;
        }
        const id =
            typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(VISITOR_KEY, id);
        document.cookie = `${VISITOR_KEY}=${encodeURIComponent(id)}; path=/; max-age=${FIRST_TTL_SECONDS}; SameSite=Lax`;
        return id;
    } catch {
        return "";
    }
}

/**
 * GA4's own client id, read from the `_ga` cookie (format `GA1.1.<id>.<ts>` —
 * the client id is the last two dot-parts). Carrying it into PandaDoc metadata
 * is what lets the warehouse join a signed contract to its GA4 sessions, which
 * no click ID can do for organic traffic. Empty string when GA has not set the
 * cookie yet (e.g. the container has not loaded because the visitor has not
 * interacted).
 */
export function getGaClientId(): string {
    try {
        const raw = readRawCookie("_ga");
        if (!raw) return "";
        const parts = raw.split(".");
        return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : "";
    } catch {
        return "";
    }
}

export function clearAttribution(): void {
    try {
        localStorage.removeItem(FIRST_KEY);
        localStorage.removeItem(LAST_KEY);
        document.cookie = `${COOKIE_KEY}=; path=/; max-age=0`;
    } catch {
        // ignore
    }
}
